<template>
  <!-- The stage. The frame renders at a fixed desktop size, so the app inside always has a permanent
       navigation drawer and nothing about its layout depends on the reader's window. The camera is
       one transform on the layer holding the frame: fitting it to the stage and zooming onto a region
       are the same `translate() scale()`. -->
  <div ref="stageRef" class="vhelp__stage" :style="stageStyle">
    <!-- The camera, in two layers: this one carries the frame and sits *under* the text, the second
         one — further down — carries the dot and the pointer and sits *over* it. Both get the same
         transform in the same render, so they move in lockstep. One layer would have put the pointer
         under the text bubble, because a transformed element is its own stacking level: a cursor that
         slides behind a tooltip is not a cursor. -->
    <div class="vhelp__camera" :style="cameraStyle">
      <!-- `pointer-events: none`: the reader moves through the walkthrough with the dot and the
           timeline, not by driving a second copy of the app. It also means the sample data cannot be
           edited, and the frame cannot be navigated out from under a step. -->
      <!-- Keyed by the load count: a reload onto the URL the frame first opened on leaves `src` as it
           was, and only a new element makes the browser load it again. -->
      <!-- `name`: survives any load inside the frame, so an instance there boots sealed even at a URL that
           lost the embed flag. See services/viewHelp/embed.ts. -->
      <iframe
        :key="frameLoads"
        ref="frameRef"
        :name="VIEW_HELP_FRAME_NAME"
        class="vhelp__frame"
        :src="frameSrc"
        :title="t('help.aria.frame')"
        :width="FRAME_WIDTH"
        :height="FRAME_HEIGHT"
        tabindex="-1"
        @load="onLoad"
      />
    </div>

    <!-- The text, over the frame beside the dot. Outside the camera, so it never scales: its position
         is worked out from where the camera will end up, and it only appears once the camera is
         there. Clicking it moves on, the same as clicking the dot. -->
    <transition name="vhelp-bubble">
      <div
        v-if="floating && landed"
        ref="bubbleRef"
        class="vhelp__bubble"
        :style="bubbleStyle"
        @click="advance"
        @mouseenter="setHold(true)"
        @mouseleave="setHold(false)"
      >
        <div v-if="arrowStyle" class="vhelp__arrow" :class="`vhelp__arrow--${side}`" :style="arrowStyle" />
        <p class="vhelp__text" aria-live="polite">{{ text }}</p>
        <p v-if="continueHint" class="vhelp__continue">{{ continueHint }}</p>
      </div>
    </transition>

    <!-- The second camera layer, over the text. See the first one. -->
    <div class="vhelp__camera vhelp__camera--above" :style="cameraStyle">
      <!-- One dot, the current step's, in the frame's own coordinates so it rides the camera. Two
           elements on purpose. The outer one is what Vue fades in and out, and carries only a
           position. The inner one carries the counter-scale — and Vue times an element's leave from
           the transitions on that element. With both on one element, the old dot was kept on screen
           for the whole of the next camera move, riding it, well after its text had already gone. -->
      <transition name="vhelp-dot">
        <div v-if="shownDot" :key="stepIndex" class="vhelp__dot-anchor" :style="dotPositionStyle">
          <div class="vhelp__dot-scale" :style="counterScaleStyle">
            <button
              type="button"
              class="vhelp__dot"
              :aria-label="dotLabel"
              @click="advance"
              @mouseenter="setHold(true)"
              @mouseleave="setHold(false)"
              @focus="setHold(true)"
              @blur="setHold(false)"
            >
              <span class="vhelp__dot-core" />
            </button>
          </div>
        </div>
      </transition>

      <lew-view-help-pointer :counter-scale="counterScaleStyle" />
    </div>

    <!-- Example pages — what a print looks like, which the walkthrough can show but never produce —
         side by side. Over both camera layers, so the pointer resting on the button that would print
         does not sit on a page. The step's text is their caption; on a stage too small for that it
         docks as usual. -->
    <transition name="vhelp-sheet">
      <div
        v-if="landed && pages.length"
        :key="stepIndex"
        class="vhelp__sheet"
        @click="advance"
      >
        <div class="vhelp__sheet-pages">
          <img v-for="page in pages" :key="page.url" class="vhelp__sheet-page" :src="page.url" :alt="t(page.alt)" />
        </div>
        <!-- The hold is the caption's, not the whole sheet's: the sheet covers the stage, and a pointer
             resting anywhere on it stood the walkthrough still, as the dot and the bubble only do. -->
        <div v-if="captioned" class="vhelp__sheet-caption" @mouseenter="setHold(true)" @mouseleave="setHold(false)">
          <p class="vhelp__text" aria-live="polite">{{ text }}</p>
          <p v-if="continueHint" class="vhelp__continue">{{ continueHint }}</p>
        </div>
      </div>
    </transition>

    <!-- Only for a page load: there is genuinely nothing to show yet. Moving between steps is an
         animation, which is the thing to look at, and gets no spinner. -->
    <transition name="vhelp-fade">
      <div v-if="isFrameLoading" class="vhelp__loading">
        <v-progress-circular color="primary" indeterminate size="32" />
      </div>
    </transition>

    <!-- Whatever the dialog wants over the picture — the feedback a key leaves. -->
    <slot />
  </div>

  <!-- A host too small to hold the text over the frame gets it underneath instead — but only once there
       is a step to show, so nothing appears here while the frame is still loading and then vanishes
       when it arrives. -->
  <div v-if="!floating && !captioned && landed && !endShown" class="vhelp__docked" @click="advance">
    <p class="vhelp__text" aria-live="polite">{{ text }}</p>
    <p v-if="continueHint" class="vhelp__continue">{{ continueHint }}</p>
  </div>
</template>

<script setup lang="ts">
/**
 * The walkthrough's picture: the real app in a frame, the camera over it, the dot and the text.
 *
 * The player (`composables/viewHelp/`) says where the camera looks and how long it takes; this
 * animates it, fits the fixed-size frame to whatever room the dialog has, and places the text beside
 * the dot. Mounted only while a walkthrough is open, so nothing here outlives one.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import router from "@/router"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { useViewHelpCopy } from "@/composables/viewHelp/useViewHelpCopy"
import { VIEW_HELP_FRAME_NAME, viewHelpEmbedUrl } from "@/services/viewHelp/embed"
import { arrowStyleFor, placeBubble, type BubbleSide } from "@/services/viewHelp/bubble"
import {
  FRAME_HEIGHT,
  FRAME_WIDTH,
  cameraFor,
  easeCamera,
  interpolateCamera,
  px,
  type Camera,
} from "@/services/viewHelp/camera"

const emit = defineEmits<{
  /** A key the reader pressed while focus was inside the frame. See `useViewHelpKeyboard`. */
  "frame-keydown": [event: KeyboardEvent]
}>()

/** Fallback width used only before the bubble has laid out; its real width lives in CSS. */
const BUBBLE_WIDTH = 280

/** Below this the stage cannot hold the text over the frame, so it docks underneath instead. */
const FLOAT_MIN_STAGE_WIDTH = 640
const FLOAT_MIN_STAGE_HEIGHT = 380

const {
  isFrameLoading,
  landed,
  endShown,
  step,
  steps,
  stepIndex,
  cameraMs,
  focusCap,
  frameLoadTarget,
  frameLoads,
  focusRect,
  dot,
  onFrameLoaded,
  advance,
  setHold,
  remeasure,
} = useViewHelp()
const { t, text, continueHint, dotLabel } = useViewHelpCopy()
const { locale } = useI18n()

const stageRef = ref<HTMLElement | null>(null)
const frameRef = ref<HTMLIFrameElement | null>(null)
const bubbleRef = ref<HTMLElement | null>(null)

const fit = ref(1)
const stageWidth = ref(0)
const bubbleStyle = ref<Record<string, string>>({})
const arrowStyle = ref<Record<string, string> | null>(null)
const side = ref<BubbleSide>("bottom")

/**
 * Where the dot is drawn beside floating text, in frame coordinates: decided with the text's side
 * (`placeBubble`), so the dot is on the edge the text is on. Null until the text is placed, so the
 * dot does not appear in the region's corner first and then jump.
 */
const dotBesideText = ref<{ x: number; y: number } | null>(null)

let stageObserver: ResizeObserver | null = null

/**
 * The frame's URL, built from `frameLoadTarget` and deliberately **not** from the route the app has
 * navigated itself to: binding `src` to that would make Vue reload the iframe every time the pointer
 * clicked a tab, and throw away the transition the reader just watched.
 */
const frameSrc = computed(() => {
  const target = frameLoadTarget.value
  if (!target) return ""
  const { fullPath } = router.resolve({
    name: target.name,
    params: target.params,
    query: target.query,
    hash: target.hash ? `#${target.hash}` : "",
  })
  return viewHelpEmbedUrl(fullPath)
})

// ── fitting the frame to the stage ────────────────────────────────────────────

const stageHeight = computed(() => FRAME_HEIGHT * fit.value)

/** Never fitted up past 1: a frame blown up past its own pixels is just blurry. */
function measureStage() {
  const available = stageRef.value?.clientWidth ?? 0
  stageWidth.value = available
  fit.value = available > 0 ? Math.min(1, available / FRAME_WIDTH) : 1
}

const stageStyle = computed(() => ({ height: `${Math.round(stageHeight.value)}px` }))

// ── the camera ────────────────────────────────────────────────────────────────

/**
 * Where the camera is going: the region, zoomed and centred as far as the frame's edges allow. The
 * lower cap while the pointer is working comes from `focusCap`.
 */
const cameraTarget = computed<Camera>(() => cameraFor(focusRect.value, fit.value, focusCap.value ?? undefined))

/**
 * Where the camera is, this frame. Animated towards `cameraTarget` in JavaScript rather than by a CSS
 * transition, because the path is the point: a transition interpolates `translate()` and `scale()`
 * each on its own, which zooms about whatever point happens to stay fixed — pinned to the top edge
 * vertically and off the stage horizontally when zooming out from a tab — so the picture grew one way
 * across and another way down. `interpolateCamera` moves what the camera looks at in a straight line
 * and changes the zoom by an even ratio. See services/viewHelp/camera.ts.
 */
const cameraNow = ref<Camera>(cameraFor(null, 1))

let cameraFrame = 0

/** Moves the camera to `to` over `cameraMs`, from wherever it is — including mid-move. */
function animateCamera(to: Camera) {
  if (cameraFrame) cancelAnimationFrame(cameraFrame)
  const from = { ...cameraNow.value }
  const duration = cameraMs.value
  if (duration <= 0) {
    cameraNow.value = to
    return
  }

  const started = performance.now()
  const frame = (now: number) => {
    const progress = Math.min(1, (now - started) / duration)
    cameraNow.value = interpolateCamera(from, to, easeCamera(progress), fit.value)
    cameraFrame = progress < 1 ? requestAnimationFrame(frame) : 0
  }
  cameraFrame = requestAnimationFrame(frame)
}

/** Straight to the target, no move — for a resize, where a glide would only look like lag. */
function snapCamera() {
  if (cameraFrame) cancelAnimationFrame(cameraFrame)
  cameraFrame = 0
  cameraNow.value = cameraTarget.value
}

const cameraStyle = computed(() => ({
  width: `${FRAME_WIDTH}px`,
  height: `${FRAME_HEIGHT}px`,
  transform: `translate(${px(cameraNow.value.tx)}, ${px(cameraNow.value.ty)}) scale(${cameraNow.value.s})`,
}))

/**
 * Undoes the camera's scale on something riding it, so a dot or the pointer stays its own size
 * however far the camera has zoomed. Computed from the same frame's camera, so it is exact throughout
 * a move.
 */
const counterScaleStyle = computed(() => ({ transform: `scale(${1 / cameraNow.value.s})` }))

/** A point in frame coordinates, as it will sit on the stage once the camera has arrived. */
const onStage = (x: number, y: number) => ({
  x: x * cameraTarget.value.s + cameraTarget.value.tx,
  y: y * cameraTarget.value.s + cameraTarget.value.ty,
})

/** A point on the stage, back in frame coordinates, for the camera the text was placed at. */
const offStage = (x: number, y: number) => ({
  x: (x - cameraTarget.value.tx) / cameraTarget.value.s,
  y: (y - cameraTarget.value.ty) / cameraTarget.value.s,
})

/** The dot as drawn: beside floating text once it is placed, in the region's corner for docked text. */
const shownDot = computed(() => {
  if (!dot.value) return null
  return floating.value ? dotBesideText.value : dot.value
})

/**
 * Where the dot is, and nothing else. No scale and above all no transition: this is the element Vue
 * fades out, and it times that fade from the transitions it finds here. See the template.
 */
const dotPositionStyle = computed(() => {
  if (!shownDot.value) return {}
  return { transform: `translate(${px(shownDot.value.x)}, ${px(shownDot.value.y)})` }
})

// ── the text beside the dot ───────────────────────────────────────────────────

const canFloat = computed(
  () => stageWidth.value >= FLOAT_MIN_STAGE_WIDTH && stageHeight.value >= FLOAT_MIN_STAGE_HEIGHT
)

/** A step with no region has no dot to sit beside, so its text docks. */
const floating = computed(() => canFloat.value && focusRect.value !== null)

// ── example pages ─────────────────────────────────────────────────────────────

/** A file under `public/`, wherever the app is served from; `{locale}` is the reader's language. */
const publicUrl = (src: string) => `${import.meta.env.BASE_URL}${src.replace("{locale}", locale.value)}`

/** The current step's example pages, in the order they stand side by side. Empty for most steps. */
const pages = computed(() => (step.value?.images ?? []).map(page => ({ url: publicUrl(page.src), alt: page.alt })))

/** The pages carry the text as their caption — unless the stage is too small, where it docks. */
const captioned = computed(() => canFloat.value && pages.value.length > 0)

// Fetched as soon as the tour is known, so the pages are there when their step lands rather than
// drawing themselves in line by line under the caption.
watch(
  [steps, locale],
  ([list]) => {
    for (const candidate of list) {
      for (const page of candidate.images ?? []) new Image().src = publicUrl(page.src)
    }
  },
  { immediate: true }
)

/** Its height is measured, never assumed: German copy wraps unpredictably. */
async function positionBubble() {
  if (!floating.value || !landed.value || !focusRect.value || !dot.value) return

  bubbleStyle.value = { visibility: "hidden" }
  await nextTick()

  const size = {
    width: bubbleRef.value?.offsetWidth || BUBBLE_WIDTH,
    height: bubbleRef.value?.offsetHeight ?? 0,
  }
  const topLeft = onStage(focusRect.value.left, focusRect.value.top)
  const region = {
    left: topLeft.x,
    top: topLeft.y,
    width: focusRect.value.width * cameraTarget.value.s,
    height: focusRect.value.height * cameraTarget.value.s,
  }
  const stage = { width: stageWidth.value, height: stageHeight.value }

  const dotOnStage = onStage(dot.value.x, dot.value.y)
  const placed = placeBubble(region, size, stage, step.value?.placement, dotOnStage)
  side.value = placed.side
  bubbleStyle.value = { top: px(placed.top), left: px(placed.left) }
  arrowStyle.value = arrowStyleFor(placed, placed.dot, size)
  dotBesideText.value = offStage(placed.dot.x, placed.dot.y)
}

// ── the frame ─────────────────────────────────────────────────────────────────

/**
 * Hands the frame's window over once it has loaded. Same origin, so its document is readable — which
 * is what makes this possible at all: regions are measured off the real elements, using the same
 * `data-help` anchors the views carry.
 */
function onLoad() {
  let win: Window | null = null
  try {
    win = frameRef.value?.contentWindow ?? null
    // Touch the document, so a frame that is somehow cross-origin fails here and not later.
    void win?.document
  } catch {
    win = null
  }
  // Capture phase, so the player sees a key before the app inside does.
  if (win) win.addEventListener("keydown", event => emit("frame-keydown", event), true)
  onFrameLoaded(win)
}

// ── keeping it all in step ────────────────────────────────────────────────────

/** The fit the camera was last moved at, so a resize can be told apart from a move. */
let cameraFit = fit.value

watch(cameraTarget, to => {
  // A resize changes the fit, and with it every camera. Following that with a glide would only look
  // like the page lagging behind the window, so it snaps; anything else is a move, and moves.
  if (fit.value !== cameraFit) {
    cameraFit = fit.value
    snapCamera()
    return
  }
  animateCamera(to)
})

// A new step's dot waits for its text to be placed. Registered before the watch below, so the reset
// runs first in the same flush.
watch(stepIndex, () => {
  dotBesideText.value = null
})

watch([landed, stepIndex, focusRect, fit], () => {
  if (landed.value) void positionBubble()
})

onMounted(() => {
  measureStage()
  if (typeof ResizeObserver !== "undefined" && stageRef.value) {
    stageObserver = new ResizeObserver(() => {
      measureStage()
      remeasure()
      void positionBubble()
    })
    stageObserver.observe(stageRef.value)
  }
})

onBeforeUnmount(() => {
  if (cameraFrame) cancelAnimationFrame(cameraFrame)
  stageObserver?.disconnect()
  stageObserver = null
})
</script>

<style scoped>
.vhelp__stage {
  position: relative;
  flex: none;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}

/* Fit and zoom are one transform from the top-left corner, so a point measured inside the frame maps
   onto the stage by one multiply and one add, and everything riding the camera moves with it. */
.vhelp__camera {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  will-change: transform;
}

/* Above the text bubble (4), under the loading cover (6). Transparent to the mouse except for the dot,
   so the reader can still click the text and the frame's area around it. */
.vhelp__camera--above {
  z-index: 5;
  pointer-events: none;
}

.vhelp__frame {
  display: block;
  border: 0;
  pointer-events: none;
  background: rgb(var(--v-theme-background));
}

/* ── the dot ───────────────────────────────────────────────────────────── */

.vhelp__dot-anchor {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
}

/* A transformed element is the containing block for its absolutely positioned children, so the
   button's -18px offset is measured from here, around the anchor point. */
.vhelp__dot-scale {
  transform-origin: 0 0;
}

/* The button is the hit area, larger than the dot you see, centred on the anchor point. */
.vhelp__dot {
  pointer-events: auto;
  position: absolute;
  top: -18px;
  left: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

/* Two halos, a beat apart, so the pulse reads as continuous rather than as a blink. */
.vhelp__dot::before,
.vhelp__dot::after {
  content: "";
  position: absolute;
  inset: 9px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  opacity: 0;
  animation: vhelp-pulse 2s cubic-bezier(0.2, 0.6, 0.35, 1) infinite;
}

.vhelp__dot::after {
  animation-delay: 1s;
}

.vhelp__dot-core {
  position: relative;
  width: 14px;
  height: 14px;
  border: 2px solid rgb(var(--v-theme-surface));
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.vhelp__dot:hover .vhelp__dot-core,
.vhelp__dot:focus-visible .vhelp__dot-core {
  transform: scale(1.35);
  box-shadow:
    0 0 0 5px rgba(var(--v-theme-primary), 0.25),
    0 2px 8px rgba(0, 0, 0, 0.35);
}

/* The pulse is an invitation; once the reader is on the dot it has done its job. */
.vhelp__dot:hover::before,
.vhelp__dot:hover::after,
.vhelp__dot:focus-visible::before,
.vhelp__dot:focus-visible::after {
  animation-play-state: paused;
  opacity: 0;
}

.vhelp__dot:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

@keyframes vhelp-pulse {
  0% {
    opacity: 0.55;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(3.2);
  }
}

.vhelp-dot-enter-active {
  transition: opacity 0.2s ease;
}

/* Out on the same beat as its text — one variable, set on the dialog, so the two cannot drift apart. */
.vhelp-dot-leave-active {
  transition: opacity var(--vhelp-leave) ease;
}

.vhelp-dot-enter-from,
.vhelp-dot-leave-to {
  opacity: 0;
}

/* ── an example page ───────────────────────────────────────────────────── */

/* Over both camera layers (5) and under the loading cover (6). Later in the document than the upper
   camera layer, so it wins the tie. */
.vhelp__sheet {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
}

/* The row of pages takes whatever height the caption leaves; each page is as tall as that row allows
   and shares its width with the others. */
.vhelp__sheet-pages {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  min-height: 0;
}

/* A sheet of paper or a phone, never distorted: `contain` keeps its proportions whichever limit it meets
   first. */
.vhelp__sheet-page {
  display: block;
  flex: 0 1 auto;
  min-width: 0;
  height: auto;
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius: 2px;
  /* Follows what is drawn, not the box: a phone's rounded corners are transparent. */
  filter: drop-shadow(0 10px 28px rgba(0, 0, 0, 0.45));
}

.vhelp__sheet-caption {
  flex: none;
  width: 100%;
  max-width: 520px;
  padding: 11px 14px;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
}

.vhelp-sheet-enter-active {
  transition: opacity 0.22s ease;
}

/* Out on the same beat as the dot and the text. */
.vhelp-sheet-leave-active {
  transition: opacity var(--vhelp-leave) ease;
}

.vhelp-sheet-enter-from,
.vhelp-sheet-leave-to {
  opacity: 0;
}

/* ── the text ──────────────────────────────────────────────────────────── */

.vhelp__bubble {
  position: absolute;
  z-index: 4;
  width: 280px;
  max-width: calc(100% - 24px);
  padding: 11px 14px;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
  cursor: pointer;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.vhelp__bubble:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
}

.vhelp-bubble-enter-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.vhelp-bubble-leave-active {
  transition: opacity var(--vhelp-leave) ease;
}

.vhelp-bubble-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.vhelp-bubble-leave-to {
  opacity: 0;
}

.vhelp__arrow {
  position: absolute;
  width: 10px;
  height: 10px;
  background: rgb(var(--v-theme-surface));
  transform: rotate(45deg);
}

.vhelp__arrow--bottom,
.vhelp__arrow--top {
  margin-left: -5px;
}

.vhelp__arrow--left,
.vhelp__arrow--right {
  margin-top: -5px;
}

.vhelp__text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.vhelp__continue {
  margin: 6px 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.vhelp__continue::after {
  content: " →";
}

.vhelp__docked {
  flex: none;
  padding: 12px 18px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  cursor: pointer;
}

.vhelp__loading {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-background));
}

.vhelp-fade-leave-active {
  transition: opacity 0.25s ease;
}

.vhelp-fade-leave-to {
  opacity: 0;
}

/* The dot stops pulsing; the camera cuts instead of gliding (`cameraMs` is 0, see the player). */
@media (prefers-reduced-motion: reduce) {
  .vhelp__dot::before,
  .vhelp__dot::after {
    animation: none;
  }

  .vhelp__bubble,
  .vhelp-sheet-enter-active {
    transition: none;
  }
}
</style>
